import { useToast } from '@group-link-one/gl-components';
import { yupResolver } from '@hookform/resolvers/yup';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { createLoginSchema } from '../schemas/login.schema';
import { loginUser } from '../Services';
import type { LoginFormData } from '../Types/LoginForm.types';
import { useLoginTranslation } from './useLoginTranslation';
import { resolveLoginError } from '../utils/resolveLoginError';

interface UseLoginReturn {
  // Form control
  register: ReturnType<typeof useForm<LoginFormData>>['register'];
  handleSubmit: ReturnType<typeof useForm<LoginFormData>>['handleSubmit'];
  errors: ReturnType<typeof useForm<LoginFormData>>['formState']['errors'];
  isSubmitting: boolean;

  // Password visibility
  showPassword: boolean;
  toggleShowPassword: () => void;

  // Submit handler
  onSubmit: (data: LoginFormData) => Promise<void>;
}

/**
 * Hook for login form logic with react-hook-form and yup validation
 * @returns Login form control and handlers
 */
export const useLogin = (): UseLoginReturn => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const { addToast } = useToast();
  const { customLocale } = useLoginTranslation();

  // Create validation schema with localized messages
  const loginSchema = createLoginSchema(customLocale);

  // Initialize react-hook-form with yup validation
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: yupResolver(loginSchema),
    mode: 'onBlur',
    defaultValues: {
      email: '',
      password: '',
    },
  });

  /**
   * Toggle password visibility
   */
  const toggleShowPassword = () => {
    setShowPassword(prev => !prev);
  };

  /**
   * Handle form submission
   */
  const onSubmit = async (data: LoginFormData): Promise<void> => {
    try {
      // Call login service with user credentials
      await loginUser({
        email: data.email,
        password: data.password,
      });

      // Successful login - navigate to dashboard
      // The RootRoute component will automatically redirect to Dashboard
      navigate('/');
    } catch (error) {
      // Set error for password field if login fails
      addToast({ type: 'error', title: resolveLoginError(error, customLocale) });
    }
  };

  return {
    register,
    handleSubmit,
    errors,
    isSubmitting,
    showPassword,
    toggleShowPassword,
    onSubmit,
  };
};
